import { action, computed, makeObservable, observable } from 'mobx';
import type CorpUserModel from '~/models/corpUser';
import {
    concat,
    filter,
    forEach,
    formatMemberField,
    getApi,
    getMemberFieldMap,
    indexOf,
    map,
    post,
    sortBy,
    toOldSysMemberFieldKey
} from '~/utils';
import { API } from '~/constants';
import { MemberFieldApiData, MemberFieldSource } from '@libs/shared';

export default class MemberFieldModel {
    memberFields: MemberFieldApiData[] = [];
    pendingRequest: Promise<void> | null = null;

    constructor() {
        makeObservable(this, {
            memberFields: observable.ref,
            personalFieldAuth: computed,
            customFields: computed,
            customFieldMap: computed,
            presetFields: computed,
            presetFieldMap: computed,
            presetAndCustomFields: computed,
            presetAndCustomFieldMap: computed,
            init: action,
            setMemberFields: action,
            sortMemberFields: action,
            addMemberField: action,
            updateMemberField: action,
            removeMemberField: action
        });
    }

    get personalFieldAuth() {
        const permissionMap: Record<string, MemberFieldApiData['account_setting']> = {};
        forEach(this.memberFields, (field) => {
            permissionMap[toOldSysMemberFieldKey(field.widget_name)] = field.account_setting;
        });
        return permissionMap;
    }

    get customFields() {
        return map(
            filter(this.memberFields, (field) => field.source === MemberFieldSource.Custom),
            formatMemberField
        );
    }

    get customFieldMap() {
        return getMemberFieldMap(this.customFields);
    }

    get presetFields() {
        return map(
            filter(this.memberFields, (field) => field.source === MemberFieldSource.Preset),
            formatMemberField
        );
    }

    get presetFieldMap() {
        return getMemberFieldMap(this.presetFields);
    }

    get presetAndCustomFields() {
        return concat(this.presetFields, this.customFields);
    }

    get presetAndCustomFieldMap() {
        return {
            ...this.presetFieldMap,
            ...this.customFieldMap
        };
    }

    init(corpUser: CorpUserModel) {
        if (!corpUser.tenantId) {
            this.pendingRequest = null;
            this.setMemberFields([]);
            return Promise.resolve();
        }
        if (this.pendingRequest) {
            return this.pendingRequest;
        }
        const api = corpUser.hasSysAdmin
            ? API.corp.member_field.list
            : API.corp.member_field.list_view;
        this.pendingRequest = post(getApi(api, corpUser.tenantId))
            .then((res) => {
                this.setMemberFields(res.data.field_list ?? []);
            })
            .finally(() => {
                this.pendingRequest = null;
            });
        return this.pendingRequest;
    }

    setMemberFields(memberFields: MemberFieldApiData[]) {
        this.memberFields = memberFields;
    }

    sortMemberFields(widgetNameList: string[]) {
        this.memberFields = sortBy(this.memberFields, (field) => {
            const index = indexOf(widgetNameList, field.widget_name);
            return index < 0 ? Number.MAX_SAFE_INTEGER : index;
        });
    }

    addMemberField(newField: MemberFieldApiData) {
        this.memberFields = [...this.memberFields, newField];
    }

    updateMemberField(editData: Partial<MemberFieldApiData>) {
        this.memberFields = map(this.memberFields, (field) => {
            if (field.widget_name === editData.widget_name) {
                return {
                    ...field,
                    ...editData
                };
            }
            return field;
        });
    }

    removeMemberField(widgetName: string) {
        this.memberFields = filter(this.memberFields, (field) => field.widget_name !== widgetName);
    }
}
